// Note: These imports are for future backend API integration
// import { generateClient } from 'aws-amplify/api';
// import { updateUserAttributes } from 'aws-amplify/auth';

// Import the utility function for bilingual category labels
import { getAwardCategoryLabel } from '../config/2025/appConfig';
import { cognitoUserService, CognitoUser } from './cognitoUserService';
import { judgeAssignmentService } from './judgeAssignmentService';

// const client = generateClient();

export interface JudgeUser {
  id: string;
  email: string;
  fullName?: string;
  assignedCategories: string[];
  isActive: boolean;
}

export interface CategoryAssignment {
  judgeEmail: string;
  categories: string[];
}

class JudgeService {

  /**
   * Get all users with judge role
   */
  async getJudgeUsers(): Promise<JudgeUser[]> {
    try {

      // First, let's test if the Lambda function is working by trying to get all users
      //await cognitoUserService.getUsersByGroup('all');

      // Now try to get just JUDGE users
      const cognitoUsers = await cognitoUserService.getJudgeUsers();

      // Convert CognitoUser to JudgeUser format
      const judgeUsers: JudgeUser[] = await Promise.all(
        cognitoUsers.map(async (cognitoUser: CognitoUser) => {
          // Get assigned categories from database (set by super admin)
          const assignedCategories = await judgeAssignmentService.getAssignedCategories(cognitoUser.email);

          return {
            id: cognitoUser.id,
            email: cognitoUser.email,
            fullName: cognitoUser.fullName,
            assignedCategories: assignedCategories,
            isActive: cognitoUser.isActive
          };
        })
      );

      return judgeUsers;
    } catch (error) {
      console.error('Error getting judge users:', error);
      return [];
    }
  }

  /**
   * Get all users (for potential judge assignment)
   */
  async getAllUsers(): Promise<JudgeUser[]> {
    try {

      // Fetch all users from Cognito
      const cognitoUsers = await cognitoUserService.getUsersByGroup('all');

      // Convert CognitoUser to JudgeUser format
      const allUsers: JudgeUser[] = await Promise.all(
        cognitoUsers.map(async (cognitoUser: CognitoUser) => {
          // Get assigned categories from database (set by super admin)
          const assignedCategories = await judgeAssignmentService.getAssignedCategories(cognitoUser.email);

          return {
            id: cognitoUser.id,
            email: cognitoUser.email,
            fullName: cognitoUser.fullName,
            assignedCategories: assignedCategories,
            isActive: cognitoUser.isActive
          };
        })
      );

      return allUsers;
    } catch (error) {
      console.error('Error getting all users:', error);
      return [];
    }
  }


  /**
   * Assign categories to a judge
   */
  async assignCategoriesToJudge(judgeEmail: string, categories: string[], assignedBy: string = 'super-admin'): Promise<void> {
    try {
      // Store categories in database (DynamoDB with localStorage fallback)
      await judgeAssignmentService.assignCategoriesToJudge(judgeEmail, categories, assignedBy);

      // TODO: Implement proper admin backend API for cross-user Cognito attribute updates
      // For now, we skip the Cognito assignment to avoid cross-user permission errors
      console.log(`Categories stored in database for judge ${judgeEmail}:`, categories);
    } catch (error) {
      console.error('Error assigning categories to judge:', error);
      throw error;
    }
  }

  /**
   * Remove categories from a judge
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async removeCategoriesFromJudge(_judgeEmail: string, _categories: string[]): Promise<void> {
    try {
      // TODO: Implement category removal logic
      // console.log(`Removing categories from judge ${_judgeEmail}:`, _categories);

      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));

      // console.log('Categories removed successfully');
    } catch (error) {
      console.error('Error removing categories from judge:', error);
      throw error;
    }
  }

  /**
   * Get categories assigned to a specific judge
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async getJudgeCategories(_judgeEmail: string): Promise<string[]> {
    try {
      // TODO: Implement get categories logic
      // console.log(`Getting categories for judge: ${_judgeEmail}`);

      // This would typically call a backend API
      // For now, return empty array
      return [];
    } catch (error) {
      console.error('Error getting judge categories:', error);
      throw error;
    }
  }

  /**
   * Get assigned categories for a specific judge
   */
  async getAssignedCategories(judgeEmail: string): Promise<string[]> {
    try {
      return await judgeAssignmentService.getAssignedCategories(judgeEmail);
    } catch (error) {
      console.error('Error getting assigned categories:', error);
      return [];
    }
  }

  /**
   * Get all available award categories
   */
  getAvailableCategories(): Array<{ value: string; label: string }> {
    return [
      { value: 'arts', label: getAwardCategoryLabel('arts') },
      { value: 'epay', label: getAwardCategoryLabel('epay') },
      { value: 'edtech', label: getAwardCategoryLabel('edtech') },
      { value: 'esgproduct', label: getAwardCategoryLabel('esgproduct') },
      { value: 'esgproject', label: getAwardCategoryLabel('esgproject') },
      { value: 'esgsolution', label: getAwardCategoryLabel('esgsolution') },
      { value: 'geron', label: getAwardCategoryLabel('geron') },
      { value: 'health', label: getAwardCategoryLabel('health') },
      { value: 'marTech', label: getAwardCategoryLabel('marTech') },
      { value: 'proTech', label: getAwardCategoryLabel('proTech') },
      { value: 'business', label: getAwardCategoryLabel('business') },
      { value: 'enviro', label: getAwardCategoryLabel('enviro') },
      { value: 'gov', label: getAwardCategoryLabel('gov') },
      { value: 'mobility', label: getAwardCategoryLabel('mobility') },
      { value: 'management', label: getAwardCategoryLabel('management') },
      { value: 'wearable', label: getAwardCategoryLabel('wearable') },
      { value: 'metaverse', label: getAwardCategoryLabel('metaverse') },
    ];
  }
}

export const judgeService = new JudgeService();
